import type { ProductDetail, ProductFeatureInput, ProductInput } from '../api/business/types';
import type { MessageKey } from '../i18n/sk';

/** A product's value of one feature group as the form edits it: none, a predefined value, or its own text. */
export type FeatureDraft = {
  mode: 'none' | 'value' | 'custom';
  valueId: string;
  /** By language, '' where there is none. */
  custom: Record<string, string>;
};

/** Where a product sits in the company's taxonomy, as its form edits it (R0030 WP-06). */
export type ClassificationDraft = {
  categoryIds: Array<string>;
  mainCategoryId: string | null;
  /** '' — no brand. */
  brandId: string;
  tagIds: Array<string>;
  /** Tags typed that the company does not have yet — made when the product is saved. */
  newTags: Array<string>;
  /** By feature group id. */
  features: Record<string, FeatureDraft>;
};

export type ClassificationResult =
  { input: Partial<ProductInput>; problem: null } | { input: null; problem: MessageKey };

export const NO_FEATURE: FeatureDraft = { mode: 'none', valueId: '', custom: {} };

export const classificationOf = (
  product: ProductDetail | null,
  languages: Array<string>,
): ClassificationDraft => ({
  categoryIds: product?.categories.map((category) => category.id) ?? [],
  mainCategoryId: product?.categories.find((category) => category.main)?.id ?? null,
  brandId: product?.brand?.id ?? '',
  tagIds: product?.tags.map((tag) => tag.id) ?? [],
  newTags: [],
  features: Object.fromEntries(
    (product?.features ?? []).map((feature) => [
      feature.groupId,
      {
        mode: feature.custom ? 'custom' : 'value',
        valueId: feature.valueId ?? '',
        custom: feature.custom
          ? Object.fromEntries(languages.map((code) => [code, feature.translations[code]?.value ?? '']))
          : {},
      },
    ]),
  ),
});

/** The categories chosen: the main one stays while it is still among them, else the first becomes it. */
export const withCategories = (
  draft: ClassificationDraft,
  categoryIds: Array<string>,
): ClassificationDraft => ({
  ...draft,
  categoryIds,
  mainCategoryId:
    draft.mainCategoryId !== null && categoryIds.includes(draft.mainCategoryId)
      ? draft.mainCategoryId
      : (categoryIds[0] ?? null),
});

const featureInputs = (
  draft: ClassificationDraft,
  languages: Array<string>,
): Array<ProductFeatureInput> | MessageKey => {
  const inputs: Array<ProductFeatureInput> = [];

  for (const [groupId, feature] of Object.entries(draft.features)) {
    if (feature.mode === 'value' && feature.valueId !== '') {
      inputs.push({ groupId, valueId: feature.valueId });
    }

    if (feature.mode === 'custom') {
      const custom = Object.fromEntries(
        languages
          .map((code) => [code, (feature.custom[code] ?? '').trim()])
          .filter(([, text]) => text !== ''),
      );

      if (custom[languages[0]] === undefined) {
        return 'classification.customDefaultRequired';
      }

      inputs.push({ groupId, custom });
    }
  }

  return inputs;
};

/**
 * What a product write sends of its place in the taxonomy: on a create what was chosen, on a change only the
 * parts that changed. `newTagIds` are the tags made from `newTags` just before.
 */
export const classificationInput = (
  draft: ClassificationDraft,
  original: ClassificationDraft | null,
  languages: Array<string>,
  newTagIds: Array<string> = [],
): ClassificationResult => {
  const features = featureInputs(draft, languages);

  if (typeof features === 'string') {
    return { input: null, problem: features };
  }

  const tagIds = [...draft.tagIds, ...newTagIds];
  const input: Partial<ProductInput> = {};
  const changed = (now: unknown, before: unknown): boolean => JSON.stringify(now) !== JSON.stringify(before);

  if (
    original === null ||
    changed(draft.categoryIds, original.categoryIds) ||
    draft.mainCategoryId !== original.mainCategoryId
  ) {
    if (original !== null || draft.categoryIds.length > 0) {
      input.categoryIds = draft.categoryIds;
      input.mainCategoryId = draft.mainCategoryId;
    }
  }

  if (original === null ? draft.brandId !== '' : draft.brandId !== original.brandId) {
    input.brandId = draft.brandId === '' ? null : draft.brandId;
  }

  if (original === null ? tagIds.length > 0 : changed(tagIds, original.tagIds)) {
    input.tagIds = tagIds;
  }

  const before = original === null ? null : featureInputs(original, languages);

  if (original === null ? features.length > 0 : changed(features, before)) {
    input.features = features;
  }

  return { input, problem: null };
};
